import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const PAD = 8;

function useRect(sel, idx) {
  const [rect, setRect] = useState(null);
  const measure = useCallback(() => {
    const el = sel && document.querySelector(sel);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [sel]);
  useLayoutEffect(() => {
    const el = sel && document.querySelector(sel);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    const t = setTimeout(measure, 450);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [sel, idx, measure]);
  return rect;
}

function Balloon({ rect, step, i, n, onNext, onBack, onClose }) {
  const w = Math.min(340, window.innerWidth - 24);
  const below = !rect || rect.bottom + 200 < window.innerHeight;
  const top = rect ? (below ? rect.bottom + PAD + 10 : Math.max(12, rect.top - PAD - 10 - 190)) : window.innerHeight / 2 - 90;
  const left = rect ? Math.min(Math.max(12, rect.left + rect.width / 2 - w / 2), window.innerWidth - w - 12) : window.innerWidth / 2 - w / 2;
  return (
    <div className="fixed z-[61] rounded-2xl p-4 shadow-2xl fade-in" style={{ top, left, width: w, background: "var(--surface)" }} data-testid="tour-balloon">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <div className="caption tnum" data-testid="tour-step-count">Passo {i + 1} de {n}</div>
          <div className="label text-base mt-0.5" data-testid="tour-step-title">{step.title}</div>
        </div>
        <button className="icon-btn" onClick={onClose} title="Fechar tour" data-testid="tour-close-button"><X size={15} /></button>
      </div>
      <p className="text-sm mt-2">{step.text}</p>
      <div className="flex items-center gap-2 mt-4">
        <div className="flex gap-1 flex-1">{Array.from({ length: n }, (_, k) => <span key={k} className="h-1.5 rounded-full transition-[width] duration-300" style={{ width: k === i ? 18 : 6, background: k <= i ? "var(--brand)" : "var(--border)" }} />)}</div>
        {i > 0 && <button className="btn btn-ghost btn-sm" onClick={onBack} data-testid="tour-back-button">Voltar</button>}
        <button className="btn btn-primary btn-sm" onClick={onNext} data-testid="tour-next-button">{i === n - 1 ? "Entendi" : "Próximo"}</button>
      </div>
    </div>
  );
}

export function Tour({ steps, open, onClose }) {
  const [i, setI] = useState(0);
  const [ready, setReady] = useState(false);
  // targets are mounted in the same commit as the tour: re-evaluate them once the page DOM exists
  useEffect(() => {
    setReady(false);
    if (!open) return undefined;
    const t = setTimeout(() => setReady(true), 300);
    return () => clearTimeout(t);
  }, [open]);
  const avail = steps.filter((s) => s.keep || !s.target || document.querySelector(s.target));
  const step = avail[i];
  const go = (n) => {
    const s = avail[n];
    if (s?.onEnter) { setReady(false); s.onEnter(); setTimeout(() => setReady(true), 400); }
    setI(n);
  };
  const rect = useRect(step?.target, i);
  useEffect(() => { if (open) setI(0); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!open || !ready || !step) return null;
  const hole = rect && { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 };
  return createPortal(
    <div data-testid="tour-overlay" data-tour-root="" style={{ pointerEvents: "auto" }}>
      <div className="fixed inset-0 z-[60]" onClick={onClose} style={hole ? undefined : { background: "rgba(0,0,0,0.55)" }} />
      {hole && <div className="fixed z-[60] rounded-xl pointer-events-none transition-all duration-300" style={{ ...hole, boxShadow: "0 0 0 9999px rgba(0,0,0,0.55), 0 0 0 3px #D6783C" }} data-testid="tour-spotlight" />}
      <Balloon rect={rect} step={step} i={i} n={avail.length} onBack={() => go(i - 1)} onNext={() => (i === avail.length - 1 ? onClose() : go(i + 1))} onClose={onClose} />
    </div>,
    document.body,
  );
}
